/* oxlint-disable @typescript-eslint/no-namespace */ //! this file contains namespaced schemas for the dbJson fields
import { z } from 'zod';
import { widgetConfigSchema as wConfigSchema } from './corso-shopify-extensions/schema.js';
import { orderEditCommitOrStage } from './crew/customer/schema/orderEdit.js';
import { exchangeRecommendation } from './crew/customer/schema/variantExchange.js';
import { claimCalculationInput as claimCalculationInputSchemaDbJson, claimCalculationResult as claimCalculationResultSchemaDbJson, claimCalculation as claimCalculationSchemaDbJson, } from './crew/schema/claimCalculation.js';
import { claimRiskAssessment as claimRiskAssessmentSchemaDbJson } from './crew/schema/claimRiskAssessment.js';
import { customFieldReferenceImages } from './crew/schema/customField.js';
import { reportDefinitionByEntity, reportFiltersByEntity, viewFilters, } from './crew/schema/filters.js';
import { Pegasus } from './crew/schema/pegasus.js';
import { shippingClaimRiskAssessment as shippingClaimRiskAssessmentSchemaDbJson } from './crew/schema/shippingClaimRiskAssessment.js';
import { Sidekick } from './crew/schema/sidekick.js';
import { discountDefinition as discountDefinitionDbJson } from './crew/schema/storeDiscountDefinition.js';
import { variant } from './crew/schema/storeProduct.js';
import { CountryCode } from './enums/countries.js';
import { helperSchema, nullifyString } from './zodExtensions.js';
export var DbJsonSchema;
(function (DbJsonSchema) {
    //! namespace relates a specific table in the DB
    let StoreAgentConversation;
    (function (StoreAgentConversation) {
        // what failed while answering, as the server saw it; never sent to the merchant app
        const failureSchema = z.object({
            at: z.string(),
            tool: z.string().nullable(),
            message: z.string(),
            cause: z.string().optional(),
            context: z.unknown().optional(),
        });
        StoreAgentConversation.storeAgentMessageMetadataSchema = Sidekick.messageMetadataSchema.extend({
            errors: z.array(failureSchema).optional(),
        });
    })(StoreAgentConversation = DbJsonSchema.StoreAgentConversation || (DbJsonSchema.StoreAgentConversation = {}));
    let Store;
    (function (Store) {
        Store.featureFlags = z.object({
            mcpServer: z.boolean().default(false).meta({
                title: 'MCP Server',
                description: 'When enabled, Settings > Developer shows the MCP Server card: how to connect Claude or ChatGPT to Corso, and the connections this user has authorized.',
            }),
        });
    })(Store = DbJsonSchema.Store || (DbJsonSchema.Store = {}));
    let StoreUiConfig;
    (function (StoreUiConfig) {
        StoreUiConfig.WarrantyProgramName = {
            warranty: 'Warranty',
            guarantee: 'Guarantee',
        };
        StoreUiConfig.customerUiConfigSchema = z.object({
            merchantPrimaryColor: z.string(),
            merchantLogoUrl: z.string(),
            merchantBackgroundUrl: z.string().nullable(),
            merchantFaviconUrl: z.string(),
            footerText: z.string(),
            isReturnPhoneRequired: z.boolean().optional(),
            isWarrantyPhoneRequired: z.boolean().optional(),
            isRegistrationPhoneRequired: z.boolean().optional(),
            //! ---- any additional properties should be added as optional
            orderHubStartRequestTitleText: helperSchema.optionalizedString,
            orderHubStartRequestDetailText: helperSchema.optionalizedString,
            warrantyProgramName: z
                .nativeEnum(StoreUiConfig.WarrantyProgramName)
                .default('Warranty')
                .optional(),
            orderNumberDescriptionText: helperSchema.optionalizedString,
            orderLookupDetailText: helperSchema.optionalizedString,
            orderLookupGiftReturnDetailText: helperSchema.optionalizedString,
            merchantBackgroundColor: helperSchema.optionalizedString,
            merchantPageTitle: helperSchema.optionalizedString,
            // refund + gift card text
            offerGiftCardDetailText: helperSchema.optionalizedString,
            offerRefundDetailText: helperSchema.optionalizedString,
            // green returns // allow customer to keep item
            noReturnRequiredItemsDetailText: helperSchema.optionalizedString,
            noReturnRequiredItemsHeadline: helperSchema.optionalizedString,
            warrantyItemsDetailText: helperSchema.optionalizedString,
            hideLabelDownloadWhenQrCodeIsPresent: z.boolean().optional(),
            // registrations
            orderLookupRegistrationText: helperSchema.optionalizedString,
            emailMarketingConsentText: helperSchema.optionalizedString,
            emailMarketingConsentDescriptionText: helperSchema.optionalizedString,
            emailMarketingConsentPolicyUrlTextTitle: helperSchema.optionalizedString,
            identifyProductUrl: helperSchema.optionalizedString,
            identifyProductUrlButtonText: helperSchema.optionalizedString,
            identifyProductDetailText: helperSchema.optionalizedString,
            identifyProductFormPlaceholder: helperSchema.optionalizedString,
            registrationSubmissionText: helperSchema.optionalizedString,
            registrationCountryCodes: z
                .array(z.nativeEnum(CountryCode))
                .default([])
                .optional(),
            // resolution component settings (picking between moneyBack, exchange, or storeCredit)
            shouldPrioritizeCredit: z.boolean().optional(),
            creditHeadlineText: helperSchema.optionalizedString,
            creditDetailText: helperSchema.optionalizedString,
            // exchange of exchange, where the credit owed is not final until review
            originatingOrderCreditTitleText: helperSchema.optionalizedString,
            originatingOrderCreditDetailText: helperSchema.optionalizedString,
            // order editing
            orderEditDescriptionText: helperSchema.optionalizedString,
            orderEditExpiredDescriptionText: helperSchema.optionalizedString,
            customCss: helperSchema.optionalizedString,
        });
        // * potentially more at home in lib, than types
        StoreUiConfig.defaultStoreUiTheme = {
            merchantPrimaryColor: '#000000',
            merchantLogoUrl: 'https://cdn.corso.com/img/crew-ui-corso-logo.svg',
            merchantBackgroundUrl: null,
            merchantFaviconUrl: 'https://cdn.corso.com/img/crew-ui-corso-logo.svg',
            footerText: '',
        };
    })(StoreUiConfig = DbJsonSchema.StoreUiConfig || (DbJsonSchema.StoreUiConfig = {}));
    let StoreSettings;
    (function (StoreSettings) {
        StoreSettings.giftCardAttributes = z.object({
            message: helperSchema.nullifiedString,
            templateSuffix: helperSchema.nullifiedString,
        });
        StoreSettings.frontReportTags = z.array(z.object({
            name: z.string(),
            id: z.string(),
            description: z.string().optional().nullable(),
        }));
        StoreSettings.orderEditingWindowOverride = z
            .object({
            label: helperSchema.nullifiedString,
            // null defers to the store's timezone; without either the override can't be placed in a week
            ianaTimezone: nullifyString(z
                .string()
                .refine((value) => Intl.supportedValuesOf('timeZone').includes(value), 'Must be a valid IANA timezone')),
            // ! min(1) rather than nonempty() so the inferred type stays number[] instead of a tuple
            daysOfWeek: z
                .array(z.number().int().min(0).max(6))
                .min(1, 'Select at least one day'), // 0 = Sunday
            startMinuteOfDay: z.number().int().min(0).max(1439),
            endMinuteOfDay: z.number().int().min(1).max(1440),
            windowMinutes: z.number().int().min(0),
        })
            .refine(({ startMinuteOfDay, endMinuteOfDay }) => endMinuteOfDay > startMinuteOfDay, {
            message: 'End time must be after start time',
            path: ['endMinuteOfDay'],
        });
        StoreSettings.orderEditingWindowOverrides = z.array(StoreSettings.orderEditingWindowOverride);
    })(StoreSettings = DbJsonSchema.StoreSettings || (DbJsonSchema.StoreSettings = {}));
    let StoreOrderLineItem;
    (function (StoreOrderLineItem) {
        StoreOrderLineItem.selectedOptionsSchema = z.array(z.object({ name: z.string(), value: z.string() }));
    })(StoreOrderLineItem = DbJsonSchema.StoreOrderLineItem || (DbJsonSchema.StoreOrderLineItem = {}));
    let StoreUiView;
    (function (StoreUiView) {
        StoreUiView.filtersSchema = viewFilters;
    })(StoreUiView = DbJsonSchema.StoreUiView || (DbJsonSchema.StoreUiView = {}));
    let ClaimLineItemShipment;
    (function (ClaimLineItemShipment) {
        ClaimLineItemShipment.customInstructionsSchema = z.object({
            fileUrl: z.url().optional(),
            fileUrlCta: z.string().optional(),
            parcelName: z.string().optional(),
        });
    })(ClaimLineItemShipment = DbJsonSchema.ClaimLineItemShipment || (DbJsonSchema.ClaimLineItemShipment = {}));
    let ClaimLineItem;
    (function (ClaimLineItem) {
        ClaimLineItem.variantExchangeLineItemSchema = variant.partial().extend({
            idFromPlatform: variant.shape.idFromPlatform,
            price: variant.shape.price,
            quantity: variant.shape.quantity,
        });
        ClaimLineItem.replacementOrderLineItems = z.object({
            replacementItems: z.array(ClaimLineItem.variantExchangeLineItemSchema),
        });
        ClaimLineItem.exchangeRecommendationSchema = exchangeRecommendation;
    })(ClaimLineItem = DbJsonSchema.ClaimLineItem || (DbJsonSchema.ClaimLineItem = {}));
    let StoreProduct;
    (function (StoreProduct) {
        StoreProduct.options = z.array(z.object({ name: z.string(), position: z.number() }));
    })(StoreProduct = DbJsonSchema.StoreProduct || (DbJsonSchema.StoreProduct = {}));
    let ShipProtectClaim;
    (function (ShipProtectClaim) {
        ShipProtectClaim.replacementItemSchema = z.object({
            // entries written before merchants could manage these have no id; readers
            // fall back to idFromPlatform so the older rows stay addressable
            id: z.string().optional(),
            name: z.string().nullish(),
            sku: z.string().nullish(),
            idFromPlatform: z.string(),
            price: z.number().nullish(),
            quantity: z.number(),
            imgUrl: z.string().nullish(),
            tax: z.null(),
            options: z
                .array(z.object({ name: z.string(), value: z.string() }))
                .optional(),
        });
        ShipProtectClaim.replacementItemDbSchema = z.object({
            items: z.array(ShipProtectClaim.replacementItemSchema),
        });
        ShipProtectClaim.riskAssessmentSchema = shippingClaimRiskAssessmentSchemaDbJson;
    })(ShipProtectClaim = DbJsonSchema.ShipProtectClaim || (DbJsonSchema.ShipProtectClaim = {}));
    let StoreProductVariant;
    (function (StoreProductVariant) {
        /** @deprecated we have migrated fully to selected options in the name value format */
        StoreProductVariant.selectedOptionsAsRecord = z.record(z.string(), z.string());
        StoreProductVariant.selectedOptionsAsNameValue = z.array(z.object({ name: z.string(), value: z.string() }));
    })(StoreProductVariant = DbJsonSchema.StoreProductVariant || (DbJsonSchema.StoreProductVariant = {}));
    let StoreShipProtectSettings;
    (function (StoreShipProtectSettings) {
        StoreShipProtectSettings.widgetConfigSchema = wConfigSchema;
    })(StoreShipProtectSettings = DbJsonSchema.StoreShipProtectSettings || (DbJsonSchema.StoreShipProtectSettings = {}));
    let Claim;
    (function (Claim) {
        Claim.claimRiskAssessmentSchema = claimRiskAssessmentSchemaDbJson;
        Claim.calculationSchema = claimCalculationSchemaDbJson;
        Claim.calculationInputSchema = claimCalculationInputSchemaDbJson;
        Claim.calculationResultSchema = claimCalculationResultSchemaDbJson;
    })(Claim = DbJsonSchema.Claim || (DbJsonSchema.Claim = {}));
    let StoreDataExportDefinition;
    (function (StoreDataExportDefinition) {
        StoreDataExportDefinition.filters = reportFiltersByEntity;
        StoreDataExportDefinition.jsonDefinition = reportDefinitionByEntity;
    })(StoreDataExportDefinition = DbJsonSchema.StoreDataExportDefinition || (DbJsonSchema.StoreDataExportDefinition = {}));
    let ClaimRiskEvent;
    (function (ClaimRiskEvent) {
        const velocityInterval = z
            .object({
            '5_minutes': z.number(),
            '1_hour': z.number(),
            '24_hours': z.number(),
        })
            .nullish();
        ClaimRiskEvent.fingerprintEventRawSchema = z
            .object({
            event_id: z.string(),
            timestamp: z.number().nullish(),
            environment_id: z.string().nullish(),
            sdk: z.object({ platform: z.string(), version: z.string() }).nullish(),
            replayed: z.boolean().nullish(),
            identification: z
                .object({
                visitor_id: z.string().nullish(),
                confidence: z
                    .object({ score: z.number(), version: z.string() })
                    .nullish(),
                visitor_found: z.boolean().nullish(),
                first_seen_at: z.number().nullish(),
                last_seen_at: z.number().nullish(),
            })
                .nullish(),
            url: z.string().nullish(),
            ip_address: z.string().nullish(),
            user_agent: z.string().nullish(),
            browser_details: z
                .object({
                browser_name: z.string().nullish(),
                browser_major_version: z.string().nullish(),
                browser_full_version: z.string().nullish(),
                os: z.string().nullish(),
                os_version: z.string().nullish(),
                device: z.string().nullish(),
            })
                .nullish(),
            bot: z.enum(['not_detected', 'good', 'bad']).nullish(),
            bot_info: z
                .object({
                name: z.string().nullish(),
                confidence: z.string().nullish(),
            })
                .nullish(),
            developer_tools: z.boolean().nullish(),
            ip_blocklist: z
                .object({
                email_spam: z.boolean().nullish(),
                attack_source: z.boolean().nullish(),
                tor_node: z.boolean().nullish(),
            })
                .nullish(),
            ip_info: z
                .object({
                v4: z.object({
                    address: z.string().nullish(),
                    geolocation: z.object({
                        accuracy_radius: z.number().nullish(),
                        latitude: z.number().nullish(),
                        longitude: z.number().nullish(),
                        postal_code: z.string().nullish(),
                        timezone: z.string().nullish(),
                        city_name: z.string().nullish(),
                        country_code: z.string().nullish(),
                        country_name: z.string().nullish(),
                        continent_code: z.string().nullish(),
                        continent_name: z.string().nullish(),
                        subdivisions: z
                            .array(z.object({
                            iso_code: z.string().nullish(),
                            name: z.string().nullish(),
                        }))
                            .nullish(),
                    }),
                    asn: z.string().nullish(),
                    asn_name: z.string().nullish(),
                    asn_network: z.string().nullish(),
                    datacenter_result: z.boolean(),
                    datacenter_name: z.string().nullish(),
                }),
            })
                .nullish(),
            proxy: z.boolean().nullish(),
            proxy_confidence: z.string().nullish(),
            incognito: z.boolean().nullish(),
            privacy_settings: z.boolean().nullish(),
            suspect_score: z.number().nullish(),
            tampering: z.boolean().nullish(),
            tampering_details: z
                .object({
                anomaly_score: z.number().nullish(),
                anti_detect_browser: z.boolean().nullish(),
            })
                .nullish(),
            velocity: z
                .object({
                distinct_ip: velocityInterval,
                distinct_country: velocityInterval,
                events: velocityInterval,
                ip_events: velocityInterval,
                // only populated when the client passes a linkedId
                distinct_linked_id: velocityInterval,
                distinct_ip_by_linked_id: velocityInterval,
                distinct_visitor_id_by_linked_id: velocityInterval,
            })
                .nullish(),
            high_activity_device: z.boolean().nullish(),
            virtual_machine: z.boolean().nullish(),
            vpn: z.boolean().nullish(),
            vpn_confidence: z.string().nullish(),
            vpn_origin_country: z.string().nullish(),
            vpn_origin_timezone: z.string().nullish(),
            vpn_methods: z
                .object({
                timezone_mismatch: z.boolean().nullish(),
                public_vpn: z.boolean().nullish(),
                auxiliary_mobile: z.boolean().nullish(),
                os_mismatch: z.boolean().nullish(),
                relay: z.boolean().nullish(),
            })
                .nullish(),
        })
            .passthrough();
    })(ClaimRiskEvent = DbJsonSchema.ClaimRiskEvent || (DbJsonSchema.ClaimRiskEvent = {}));
    let FrontReport;
    (function (FrontReport) {
        const monthlyMetricsSchema = z.object({
            avgResponseTimeMinutes: z.number().default(0),
            totalCsatScoresCollected: z.number().default(0),
            messagesSent: z.number().default(0),
            messagesReceived: z.number().default(0),
            csatScorePercent: z.number().default(0),
            newConversations: z.number().default(0),
        });
        const scoredReviewSchema = z.object({
            rating: z.number(),
            comment: z.string(),
            customerName: z.string(),
            standoutScore: z.number().default(0),
        });
        FrontReport.reportRawSchema = z.object({
            id: z.string(),
            month: z.string(),
            year: z.string(),
            fromDate: z.string(),
            toDate: z.string(),
            metrics: monthlyMetricsSchema,
            reviews: z.array(scoredReviewSchema),
        });
    })(FrontReport = DbJsonSchema.FrontReport || (DbJsonSchema.FrontReport = {}));
    let StoreDiscountDefinition;
    (function (StoreDiscountDefinition) {
        StoreDiscountDefinition.jsonDefinition = discountDefinitionDbJson;
    })(StoreDiscountDefinition = DbJsonSchema.StoreDiscountDefinition || (DbJsonSchema.StoreDiscountDefinition = {}));
    let CustomField;
    (function (CustomField) {
        CustomField.referenceImagesSchema = customFieldReferenceImages;
    })(CustomField = DbJsonSchema.CustomField || (DbJsonSchema.CustomField = {}));
    let StoreOrderFulfillment;
    (function (StoreOrderFulfillment) {
        /** @deprecated shape of the `tracking_info` blob, replaced by `store_order_fulfillment_shipment` rows */
        StoreOrderFulfillment.trackingInfo = z.array(z.object({
            trackingInfoNumber: z.string(),
            trackingInfoUrl: z.string().nullish(),
            trackingInfoCompany: z.string().nullish(),
        }));
    })(StoreOrderFulfillment = DbJsonSchema.StoreOrderFulfillment || (DbJsonSchema.StoreOrderFulfillment = {}));
    let StoreOrderEditFulfillmentHold;
    (function (StoreOrderEditFulfillmentHold) {
        StoreOrderEditFulfillmentHold.fulfillmentOrders = z.array(z.string());
    })(StoreOrderEditFulfillmentHold = DbJsonSchema.StoreOrderEditFulfillmentHold || (DbJsonSchema.StoreOrderEditFulfillmentHold = {}));
    let StoreOrderAppliedEdit;
    (function (StoreOrderAppliedEdit) {
        const money = z.object({ amount: z.string(), currencyCode: z.string() });
        // ! Indexes must match the orderEditCommitOrStage discriminated union order.
        // See packages/corso-types/src/crew/customer/schema/orderEdit.ts.
        const stagedEditsByIndex = {
            UPDATE_SHIPPING_ADDRESS: orderEditCommitOrStage.options[1].shape.stagedEdits,
            MODIFY_QUANTITY: orderEditCommitOrStage.options[3].shape.stagedEdits,
            UPDATE_SHIPPING_LINE: orderEditCommitOrStage.options[5].shape.stagedEdits,
            ADD_DISCOUNT_CODE: orderEditCommitOrStage.options[6].shape.stagedEdits,
        };
        StoreOrderAppliedEdit.detail = {
            [Pegasus.OrderEditType.CANCEL_ORDER]: z.object({
                isCancelled: z.boolean(),
                restocked: z.boolean(),
                wasCustomerNotified: z.boolean(),
            }),
            [Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS]: stagedEditsByIndex.UPDATE_SHIPPING_ADDRESS.extend({
                cancelledOriginal: z.boolean().optional(),
                recreatedOrderIdFromPlatform: z.string().optional(),
            }),
            [Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION]: z.object({
                success: z.boolean(),
            }),
            [Pegasus.OrderEditType.MODIFY_QUANTITY]: stagedEditsByIndex.MODIFY_QUANTITY.extend({
                success: z.boolean(),
                refundedAmount: money.nullish(),
                additionalPaymentCollectionUrl: helperSchema.optionalizedString,
            }),
            [Pegasus.OrderEditType.UPDATE_EMAIL]: z.object({
                previousEmail: z.email().nullable(),
                newEmail: z.email(),
            }),
            [Pegasus.OrderEditType.UPDATE_SHIPPING_LINE]: stagedEditsByIndex.UPDATE_SHIPPING_LINE.extend({
                refundedAmount: money.nullish(),
                additionalPaymentCollectionUrl: helperSchema.optionalizedString,
                previousShippingLine: stagedEditsByIndex.UPDATE_SHIPPING_LINE.shape.previousShippingLine.optional(),
            }),
            [Pegasus.OrderEditType.ADD_DISCOUNT_CODE]: stagedEditsByIndex.ADD_DISCOUNT_CODE.extend({
                success: z.boolean(),
                refundedAmount: money.nullish(),
                abandonedOn: z.string().nullish(),
            }),
        };
    })(StoreOrderAppliedEdit = DbJsonSchema.StoreOrderAppliedEdit || (DbJsonSchema.StoreOrderAppliedEdit = {}));
})(DbJsonSchema || (DbJsonSchema = {}));
//# sourceMappingURL=dbJsonSchemas.js.map